/**
 * Shared date/time utilities for Cohort DatePicker + time input components.
 *
 * The cohort scheduling UI uses a BB-UI DatePicker (date only) paired with
 * a TextField type="time" (time only). These helpers bridge the two controls
 * and enforce the business rule that the upgrade date must be at least 15
 * calendar days from today. All days of the week are selectable.
 */

/**
 * Days of the week allowed for cohort scheduling.
 * All days (0–6) are allowed.
 */
export const ALLOWED_DAYS = [0, 1, 2, 3, 4, 5, 6] as const;

/**
 * Returns `true` when the given date should be **disabled** in the DatePicker.
 * All days of the week are enabled; only the minimum-date constraint applies.
 *
 * Designed to be passed directly to the DatePicker's `shouldDisableDate` prop.
 */
export const shouldDisableDateFn = (): boolean => false;

/**
 * Combines a Date (date portion) with a time string ("HH:mm") into a single
 * Date whose hours/minutes come from the time string and whose
 * year/month/day come from the date argument.
 *
 * Returns `null` when either argument is missing or the time string is invalid.
 */
export const combineDateAndTime = (date: Date | null, time: string): Date | null => {
  if (!date || !time) return null;
  const parts = time.split(':').map(Number);
  if (parts.length < 2 || Number.isNaN(parts[0]) || Number.isNaN(parts[1])) return null;
  const [hours, minutes] = parts;
  const combined = new Date(date);
  combined.setHours(hours, minutes, 0, 0);
  return combined;
};

/**
 * Extracts the time portion of a Date as "HH:mm".
 * Returns an empty string when the date is null.
 */
export const extractTimeString = (date: Date | null): string => {
  if (!date) return '';
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
};

/**
 * Formats a Date's *local* representation to the "YYYY-MM-DDTHH:mm" string
 * that `createDateFromTz` expects as input.
 */
export const formatDateToLocalString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

/**
 * Computes the earliest selectable date: 15 calendar days from today at midnight.
 */
export const getMinSelectableDate = (minDaysFromToday = 15): Date => {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  now.setDate(now.getDate() + minDaysFromToday);
  return now;
};

/**
 * Formats a backend-supplied GMT/UTC timestamp for display in a target timezone.
 * Appends `Z` when the input lacks an explicit timezone marker so it isn't
 * misparsed as local time. Returns an empty string when the input is missing.
 *
 * When `timeZone` is omitted the browser's local timezone is used; pass an
 * IANA name (e.g. 'UTC', 'America/New_York') to render in a specific zone.
 */
export const formatGmtInZone = (value: string | null | undefined, timeZone?: string): string => {
  if (!value) return '';
  const hasZone = /Z|[+-]\d{2}:?\d{2}$/.test(value);
  const iso = hasZone ? value : `${value}Z`;
  return new Date(iso).toLocaleString(undefined, {
    dateStyle: 'short',
    timeStyle: 'short',
    ...(timeZone ? { timeZone } : {}),
  });
};
